import { useEffect, useState } from 'react';
import { Play, CheckCircle2, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

interface GdprRow {
  id: string;
  user_id: string | null;
  kind: string;
  status: string;
  notes: string | null;
  download_url: string | null;
  requested_at: string;
  completed_at: string | null;
}

const STATUSES = ['received', 'in_progress', 'completed', 'rejected'] as const;

export function GdprQueuePage() {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('received');
  const [rows, setRows] = useState<GdprRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    api<{ data: GdprRow[] }>(`/admin/gdpr-requests?status=${status}`)
      .then((res) => setRows(res.data))
      .catch(() => setRows([]));
  }
  useEffect(load, [status]);

  async function action(id: string, route: string, body?: Record<string, unknown>) {
    setBusy(id + route);
    try {
      await api(`/admin/gdpr-requests/${id}/${route}`, { method: 'POST', json: body ?? {} });
      load();
    } catch {
      setError('Action failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black tracking-tight">GDPR queue</h1>
        <p className="text-sm text-muted-foreground">Access / export / erasure workflow.</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={status === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {error !== null && (
        <p
          role="alert"
          className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            {rows?.length ?? 0} requests — {status}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rows === null && <div className="h-24 animate-pulse rounded-2xl bg-muted" />}
          {rows?.length === 0 && (
            <p className="text-sm text-muted-foreground">No requests in this state.</p>
          )}
          {rows !== null && rows.length > 0 && (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border p-3 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-bold">
                      {r.kind} — user {r.user_id ?? '(erased)'}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDate(r.requested_at)}</p>
                    {r.notes !== null && <p className="mt-1 italic">{r.notes}</p>}
                  </div>
                  <Badge variant="secondary">{r.status}</Badge>
                  <div className="flex gap-2">
                    {r.status === 'received' && (
                      <Button
                        size="sm"
                        disabled={busy !== null}
                        onClick={() => void action(r.id, 'start')}
                      >
                        <Play className="mr-1 h-4 w-4" /> Start
                      </Button>
                    )}
                    {r.status === 'in_progress' && (
                      <>
                        <Button
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => void action(r.id, 'complete')}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Complete
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy !== null}
                          onClick={() => {
                            const note = window.prompt('Reason for rejection (5–2000 chars):');
                            if (note !== null && note.length >= 5)
                              void action(r.id, 'reject', { notes: note });
                          }}
                        >
                          <XCircle className="mr-1 h-4 w-4" /> Reject
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
